import Modal from 'react-modal';
import { CloseButton } from './CloseButton.tsx';
import { useLoginSession as useLoginSession } from './SessionContext.tsx';
import { useEffect, useState } from 'react';
import { useRawLocalState, useRcAdapter } from './GlobalState.tsx';
import {
    applyTimeout,
    canReachTriplit,
    getAppStoreWebLink,
    isCandidateForApp,
    isIOS,
    MODAL_CONTENT_DEFAULTS,
    MODAL_OVERLAY_DEFAULTS,
    PREMIUM_ENTITLEMENT_NAME,
    UserCancellationError,
} from './utils.ts';
import Spinner from './Spinner.tsx';
import { PackageSummary } from './RcAdapter.ts';
import { PaywallTrigger, useSubscriptionContext } from './SubscriptionContext.tsx';
import { Capacitor } from '@capacitor/core';
import './Paywall.css';
import { useInGameNotificationContext } from './InGameNotificationContext.tsx';
import { track } from './umami.ts';
import * as Sentry from '@sentry/capacitor';
import { useAdInterfaceContext } from './AdInterface.tsx';
import AppStoreButton from './assets/app_store_download.svg';
import PlayStoreButton from './assets/play_store_download.svg';
import { AppStoreButtonLink, PlayStoreButtonLink } from './AppStoreButton.tsx';

enum PaywallState {
    LOADING,
    LOADED,
    PAYMENT_IN_PROGRESS,
    ERROR,
}

export function Paywall() {
    const rcAdapter = useRcAdapter();
    const subscriptionContext = useSubscriptionContext();
    const loginSession = useLoginSession();
    const inGameNotificationsContext = useInGameNotificationContext();
    const { isFirstVisit } = useRawLocalState();
    const adInterface = useAdInterfaceContext();

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [subscriptionLoadingState, setSubscriptionLoadingState] = useState<PaywallState>(PaywallState.LOADING);
    const [rewardAdLoadingState, setRewardAdLoadingState] = useState<PaywallState>(PaywallState.LOADED);
    const [packages, setPackages] = useState<PackageSummary[]>([]);

    function getCustomEventData() {
        return {
            newUser: isFirstVisit,
        };
    }

    function openPaywall() {
        track('paywall_displayed', getCustomEventData());
        setIsOpen(true);
    }

    function closePaywall() {
        console.info('Closing the paywall...');
        setIsOpen(false);
        setRewardAdLoadingState(PaywallState.LOADED);
    }

    function reloadPackagesIfNeeded() {
        if (subscriptionLoadingState === PaywallState.LOADED) {
            return;
        }
        loadPackages();
    }

    function loadPackages() {
        applyTimeout(rcAdapter.getPackages(), 5000)
            .then((pkgs) => {
                setPackages(pkgs);
                setSubscriptionLoadingState(PaywallState.LOADED);
            })
            .catch((e) => {
                setSubscriptionLoadingState(PaywallState.ERROR);
                console.error('[Paywall] issue in fetching packages', e);
                Sentry.captureException(e);
            });
    }

    useEffect(() => {
        loadPackages();
    }, [rcAdapter]);

    useEffect(() => {
        if (subscriptionContext.paywallTrigger != null) {
            openPaywall();
        }
    }, [subscriptionContext.paywallTrigger]);

    function completeOnboarding(alreadyPro: boolean) {
        closePaywall();
        subscriptionContext.onboardingSucceeded();
        if (!alreadyPro) {
            inGameNotificationsContext.displayNotification('You are a pro user now!', 3000);
        }
    }

    function closePaywallDueToUserCancellation() {
        track('paywall_declined', getCustomEventData());
        subscriptionContext.onboardingFailed(new UserCancellationError('User cancelled pro onboarding'));
        closePaywall();
    }

    function closeDueToUnexpectedError(e: Error) {
        subscriptionContext.onboardingFailed(e);
        closePaywall();
        console.error('Purchase error', e);
        Sentry.captureException(e);
        inGameNotificationsContext.displayNotification('There was an issue processing your payment.', 3000);
    }

    async function upgradeToPro(packageSummary: PackageSummary) {
        track(`paywall_clicked_${packageSummary.frequency}`, getCustomEventData());
        console.info('User selected package', packageSummary);
        setSubscriptionLoadingState(PaywallState.PAYMENT_IN_PROGRESS);
        try {
            const alreadyPro = await initAndCheckIfAlreadyPro();
            if (alreadyPro) {
                completeOnboarding(/* alreadyPro= */ true);
                return;
            }
            await rcAdapter.purchase(loginSession.getUserId()!, packageSummary);
            console.info(`User subscribed to package: ${packageSummary.frequency}`);
            track(`paywall_subscribed_${packageSummary.frequency}`, getCustomEventData());
            completeOnboarding(/* alreadyPro= */ false);
        } catch (e) {
            if (!(e instanceof UserCancellationError)) {
                closeDueToUnexpectedError(e);
            }
        } finally {
            setSubscriptionLoadingState(PaywallState.LOADED);
        }
    }

    async function restorePurchases() {
        try {
            // Ensure the user is logged in first
            const session = await loginSession.getOrOpenSession();
            await rcAdapter.restore(session.user.id);
            const alreadyPro = await initAndCheckIfAlreadyPro();
            if (alreadyPro) {
                completeOnboarding(/* alreadyPro= */ true);
                return;
            } else {
                inGameNotificationsContext.displayNotification('We did not find a previous purchase to restore', 3000);
            }
        } catch (e) {
            if (!(e instanceof UserCancellationError)) {
                closeDueToUnexpectedError(e);
            }
        }
    }

    async function signInFirst() {
        try {
            await initAndCheckIfAlreadyPro();
        } catch (e) {
            if (!(e instanceof UserCancellationError)) {
                closeDueToUnexpectedError(e);
            }
        }
    }

    async function initAndCheckIfAlreadyPro(): Promise<boolean> {
        console.info('Checking if user is already pro');
        // Ensure the user is logged in first
        const session = await loginSession.getOrOpenSession();
        // Ensure that the user does not already have pro
        const alreadyPro = await rcAdapter.hasEntitlement(
            session.user.id,
            PREMIUM_ENTITLEMENT_NAME,
            /* useCache= */ false,
        );
        if (alreadyPro) {
            closePaywall();
            subscriptionContext.onboardingSucceeded();
            inGameNotificationsContext.displayNotification('You are already a pro user! Welcome back', 3000);
            return true;
        } else {
            return false;
        }
    }

    function getTitle(pkg: PackageSummary) {
        if (pkg.frequency === 'monthly') {
            return 'Monthly';
        } else if (pkg.frequency === 'annual') {
            return 'Annual';
        } else {
            // Fall back to the title
            return pkg.title;
        }
    }

    function triggerToTitle(trigger: PaywallTrigger | null): string {
        switch (trigger) {
            case PaywallTrigger.ARCHIVE_ACCESS:
                return '🔒 Archived Puzzle';
            case PaywallTrigger.USER_INITIATED_UPGRADE:
            default:
                return 'Triplit Pro';
        }
    }

    return (
        <div className='paywall-container'>
            <Modal
                isOpen={isOpen}
                onAfterOpen={reloadPackagesIfNeeded}
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '250px',
                        margin: 'auto auto',
                        height: 'fit-content',
                        maxHeight: '90%',
                        overflowY: 'auto',
                        padding: '30px',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}>
                <div className='paywall-outer-container'>
                    <CloseButton onClose={closePaywallDueToUserCancellation} />
                    <h2 style={{ textAlign: 'center' }}>{triggerToTitle(subscriptionContext.paywallTrigger)}</h2>
                    <div className='paywall-grouping'>
                        <h3 className='center-text'>Upgrade to Triplit Pro</h3>
                        <ul className='features-list'>
                            <li>
                                <div>✅</div>
                                <div>Unlock the full Triplit archive</div>
                            </li>
                            <li>
                                <div>✅</div>
                                <div>Always ad-free</div>
                            </li>
                            <li>
                                <div>✅</div>
                                <div>Exclusive 💎 badge on the share image</div>
                            </li>
                        </ul>
                        {subscriptionLoadingState === PaywallState.LOADING ? (
                            <Spinner />
                        ) : subscriptionLoadingState === PaywallState.ERROR ? (
                            <div className='center-text error-text'>
                                There was an issue loading available offerings. Please try again later.
                            </div>
                        ) : (
                            <div className='packages-container'>
                                {packages.map((pkg) => (
                                    <div className='package-container' key={pkg.id}>
                                        <div className='package-column'>
                                            <div style={{ fontWeight: 'bold' }}>{getTitle(pkg)}</div>
                                            <div> {pkg.price.localized} </div>
                                        </div>
                                        <div className='package-column'>
                                            {subscriptionLoadingState === PaywallState.PAYMENT_IN_PROGRESS ? (
                                                <Spinner className='mini-spinner' />
                                            ) : (
                                                <button
                                                    className='game-button play-button button-secondary fit-content-width-button upgrade-button cursor-pointer'
                                                    onClick={() => void upgradeToPro(pkg)}>
                                                    Upgrade
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {adInterface.adsSupported() &&
                        subscriptionContext.paywallTrigger === PaywallTrigger.ARCHIVE_ACCESS &&
                        (rewardAdLoadingState === PaywallState.LOADING ? (
                            <Spinner className='mini-spinner' />
                        ) : (
                            <>
                                <div className='inline-separator'>
                                    <span>or</span>
                                </div>
                                <div className='paywall-grouping'>
                                    <h3>Play for free</h3>
                                    <p className='center-text watch-ad-container'>
                                        <button
                                            className='game-button play-button button-secondary fit-content-width-button upgrade-button cursor-pointer'
                                            onClick={async () => {
                                                track('paywall_clicked_watch_ad', getCustomEventData());
                                                try {
                                                    setRewardAdLoadingState(PaywallState.LOADING);
                                                    // Ensure that the user can connect to the Triplit server so they can
                                                    // fetch the puzzle before making them sit through an ad.
                                                    if (!(await canReachTriplit())) {
                                                        throw new Error('Cannot reach Triplit server, ad aborted');
                                                    }
                                                    await applyTimeout(
                                                        (async () => {
                                                            await adInterface.prepareArchiveUnlockInterstitialAdIfNeeded();
                                                        })(),
                                                        4000,
                                                    );
                                                    // Do this outside of the timeout, otherwise the length of the video itself will count
                                                    // against the timeout.
                                                    await adInterface.showArchiveUnlockInterstitialAd();
                                                    console.info(
                                                        '[Paywall] archive unlock interstitial ad shown successfully',
                                                    );
                                                } catch (e) {
                                                    console.error('Error showing interstitial ad from paywall:', e);
                                                    Sentry.captureException(e);
                                                    track('paywall_failed_watch_ad', getCustomEventData());
                                                    subscriptionContext.onboardingBypassed();
                                                    inGameNotificationsContext.displayNotification(
                                                        'A little treat from us. Enjoy your puzzle!',
                                                        3000,
                                                    );
                                                    closePaywall();
                                                    return;
                                                } finally {
                                                    setRewardAdLoadingState(PaywallState.LOADED);
                                                }
                                                track('paywall_completed_watch_ad', getCustomEventData());
                                                console.info('Paywall: onboarding bypassed...');
                                                subscriptionContext.onboardingBypassed();
                                                inGameNotificationsContext.displayNotification(
                                                    'Archived puzzle unlocked!',
                                                    3000,
                                                );
                                                closePaywall();
                                            }}>
                                            ▶ Watch Ad
                                        </button>
                                    </p>
                                </div>
                            </>
                        ))}

                    {!adInterface.adsSupported() &&
                        subscriptionContext.paywallTrigger === PaywallTrigger.ARCHIVE_ACCESS && (
                            <>
                                <div className='inline-separator'>
                                    <span>or</span>
                                </div>
                                <div className='paywall-grouping'>
                                    <h3 className='center-text'>Play for free on the app</h3>
                                    <p className='center-text watch-ad-container'>
                                        <div
                                            className='store-buttons-container'
                                            aria-label='Download Triplit on the App Store'
                                            onClick={() => {
                                                track('app_store_link_clicked_from_paywall', getCustomEventData());
                                            }}>
                                            <PlayStoreButtonLink /> <AppStoreButtonLink />
                                        </div>
                                    </p>
                                </div>
                            </>
                        )}

                    {!loginSession.session && (
                        <div className='center-text secondary-text'>
                            Already pro?{' '}
                            <a className='adjustment-text-link' onClick={() => void signInFirst()}>
                                Sign in
                            </a>
                        </div>
                    )}
                    {Capacitor.isNativePlatform() && (
                        <div className='center-text adjustment-text-link'>
                            <a onClick={() => void restorePurchases()}>Restore purchases</a>{' '}
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}
